import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTaxPortal } from '../../utils/useTaxPortal';
import TaxPortalLayout from '../Common/TaxPortalLayout';
import { getApiUrl } from '../../authConfig';
import { getValidToken } from '../../utils/msalTokenUtil';
import './FaxDashboard.css';
import {
  FaPaperPlane,
  FaSync,
  FaEnvelope,
  FaFilePdf,
  FaInbox,
  FaExclamationTriangle,
  FaEllipsisV
} from 'react-icons/fa';


const formatToEST = (utcDateString) => {
  if (!utcDateString) return 'N/A';
  // SQLite returns 'YYYY-MM-DD HH:MM:SS'. Replace space with 'T' and append 'Z' to parse as UTC
  const dateString = utcDateString.includes('Z') || utcDateString.includes('T') 
    ? utcDateString 
    : `${utcDateString.replace(' ', 'T')}Z`;
    
  return new Date(dateString).toLocaleString(undefined, { timeZone: 'America/New_York' });
};

const initials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join('');
};

const FaxDashboard = () => {
  const portalState = useTaxPortal();
  const { account, msalInstance, isInitialized } = portalState;
  const [faxes, setFaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resendModal, setResendModal] = useState({ show: false, fax: null, toNumber: '' });
  const [isResending, setIsResending] = useState(false);

  const fetchFaxes = useCallback(async () => {
    try {
      const accessToken = await getValidToken(msalInstance, account);
      
      const response = await fetch(getApiUrl('/v1/faxes'), {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load fax logs. Make sure you have admin access.');
      }

      const data = await response.json();
      setFaxes(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [msalInstance, account]);

  useEffect(() => {
    if (account && isInitialized) {
      fetchFaxes();
    }
  }, [account, isInitialized, fetchFaxes]);

  const handleRequestFaxLink = async () => {
    try {
      const accessToken = await getValidToken(msalInstance, account);
      
      const response = await fetch(getApiUrl('/v1/fax/generate-token'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: '' }) // Left blank so you can enter it in the modal
      });

      if (!response.ok) {
        throw new Error('Failed to generate fax token.');
      }

      const data = await response.json();
      const generatedLink = `${window.location.origin}/send-fax/${data.token}`;
      
      portalState.setEmailModalConfig({
        customer: { email: '', name: 'Client' },
        action: 'requestFaxLink',
        taxType: 'fax',
        customData: { generatedLink }
      });
    } catch (err) {
      toast.error(`Error generating fax link: ${err.message}`);
    }
  };

  const handleOpenResend = (fax) => {
    setResendModal({ show: true, fax, toNumber: fax.to_number });
  };

  const handleCloseResend = () => {
    setResendModal({ show: false, fax: null, toNumber: '' });
  };

  const handleResendSubmit = async () => {
    if (!resendModal.toNumber) {
      toast.error('Please provide a destination fax number.');
      return;
    }
    try {
      setIsResending(true);

      const documentLink = resendModal.fax.document_link;
      if (!documentLink) {
        throw new Error("Original document link is missing. Cannot locate file.");
      }

      const graphToken = await msalInstance.acquireTokenSilent({ scopes: ['Files.ReadWrite.All'], account });

      let driveId, itemId;
      let downloadUrl;

      // Strategy A: Direct Share URL resolution (Highly reliable for any web URL)
      try {
        const base64Value = btoa(unescape(encodeURIComponent(documentLink)))
          .replace(/\//g, '_')
          .replace(/\+/g, '-')
          .replace(/=+$/, '');
        const shareId = `u!${base64Value}`;

        const shareRes = await fetch(`https://graph.microsoft.com/v1.0/shares/${shareId}/driveItem`, {
          headers: { 'Authorization': `Bearer ${graphToken.accessToken}` }
        });

        if (shareRes.ok) {
          const shareData = await shareRes.json();
          driveId = shareData.parentReference?.driveId;
          itemId = shareData.id;
          downloadUrl = shareData['@microsoft.graph.downloadUrl'];
        }
      } catch (e) {
        console.warn("Share resolution failed, falling back to search", e);
      }

      // Strategy B: Fallback to Search by filename
      if (!itemId) {
        let fileName = "";
        try {
          const urlObj = new URL(documentLink);
          fileName = decodeURIComponent(urlObj.pathname.split('/').pop());
        } catch (e) {
          fileName = decodeURIComponent(documentLink.split('/').pop().split('?')[0]);
        }

        const searchPayload = {
          requests: [{
            entityTypes: ['driveItem'],
            query: { queryString: `filename:"${fileName.replace(/"/g, '')}"` }
          }]
        };

        const searchRes = await fetch('https://graph.microsoft.com/v1.0/search/query', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${graphToken.accessToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(searchPayload)
        });

        if (!searchRes.ok) throw new Error("Failed to search for the document in OneDrive.");

        const searchData = await searchRes.json();
        const hits = searchData.value?.[0]?.hitsContainers?.[0]?.hits || [];
        const matchedFiles = hits.map(hit => hit.resource).filter(item => item && !item.folder);

        if (matchedFiles.length === 0) {
           throw new Error("Could not find the original file in your OneDrive. It may have been moved or deleted.");
        }

        const selectedItem = matchedFiles[0];
        driveId = selectedItem.parentReference?.driveId;
        itemId = selectedItem.id;
      }

      if (!itemId) throw new Error("Could not resolve the document ID in OneDrive.");

      // Strategy C: Fetch item directly to get the download URL if not present
      if (!downloadUrl) {
        const getItemUrl = driveId 
          ? `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}`
          : `https://graph.microsoft.com/v1.0/me/drive/items/${itemId}`;

        const itemResponse = await fetch(getItemUrl, {
          headers: { 'Authorization': `Bearer ${graphToken.accessToken}` }
        });

        if (itemResponse.ok) {
          const itemData = await itemResponse.json();
          downloadUrl = itemData['@microsoft.graph.downloadUrl'];
        }
      }

      // Strategy D: Create a temporary anonymous view link and force download
      if (!downloadUrl) {
         const createLinkUrl = driveId 
           ? `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${itemId}/createLink`
           : `https://graph.microsoft.com/v1.0/me/drive/items/${itemId}/createLink`;
           
         const linkResponse = await fetch(createLinkUrl, {
           method: 'POST',
           headers: { 'Authorization': `Bearer ${graphToken.accessToken}`, 'Content-Type': 'application/json' },
           body: JSON.stringify({ type: 'view', scope: 'anonymous' })
         });
         
         if (linkResponse.ok) {
           const linkData = await linkResponse.json();
           const webUrl = linkData.link?.webUrl;
           if (webUrl) {
             downloadUrl = webUrl + (webUrl.includes('?') ? '&download=1' : '?download=1');
           }
         }
      }

      if (!downloadUrl) throw new Error("Could not extract a direct download URL for this file.");

      const accessToken = await getValidToken(msalInstance, account);
      const response = await fetch(getApiUrl('/v1/resend-fax'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          fax_id: resendModal.fax.fax_id, 
          to_number: resendModal.toNumber,
          download_url: downloadUrl
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to resend fax.');
      
      toast.success('Fax has been queued for resending.');
      handleCloseResend();
      fetchFaxes();
    } catch (err) {
      toast.error(`Error resending fax: ${err.message}`);
    } finally {
      setIsResending(false);
    }
  };

  if (loading) {
    return (
      <TaxPortalLayout title="Fax Dashboard" description="View sent and received faxes" portalState={portalState}>
        <div className="fax-state-panel">
          <div className="spinner-border text-primary" role="status"></div>
          <div className="mt-3 text-muted fw-semibold">Loading fax dashboard…</div>
        </div>
      </TaxPortalLayout>
    );
  }

  if (error) {
    return (
      <TaxPortalLayout title="Fax Dashboard" description="View sent and received faxes" portalState={portalState}>
        <div className="fax-error-panel">
          <FaExclamationTriangle className="fax-error-icon" aria-hidden="true" />
          <div>
            <div className="fw-bold">Something went wrong</div>
            <div className="text-muted">{error}</div>
          </div>
        </div>
      </TaxPortalLayout>
    );
  }

  return (
    <TaxPortalLayout title="Fax Dashboard" description="View sent and received faxes" portalState={portalState}>
      <div className="fax-dashboard-container mt-3">
        <div className="header-actions mb-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <div>
            <h2 className="fs-4 text-secondary fw-bold mb-1">Fax Transmission Dashboard</h2>
            <div className="text-muted small">{faxes.length} {faxes.length === 1 ? 'record' : 'records'} on file</div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button onClick={fetchFaxes} className="btn btn-outline-primary fax-btn">
              <FaSync aria-hidden="true" /> <span>Refresh</span>
            </button>
            <button onClick={handleRequestFaxLink} className="btn btn-info text-white fw-bold fax-btn">
              <FaEnvelope aria-hidden="true" /> <span>Request Fax Link</span>
            </button>
            <Link to="/send-fax" className="btn btn-primary fw-bold fax-btn">
              <FaPaperPlane aria-hidden="true" /> <span>Send Fax</span>
            </Link>
          </div>
        </div>

      <div className="d-block d-md-none">
        {faxes.length === 0 ? (
          <div className="fax-empty-state">
            <FaInbox className="fax-empty-icon" aria-hidden="true" />
            <div className="fw-bold text-dark mt-2">No faxes found</div>
            <div className="text-muted small">Sent and received faxes will appear here.</div>
          </div>
        ) : (
          faxes.map(fax => (
            <div key={fax.fax_id} className="card mb-3 shadow-sm border-0 fax-card">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <div className="fax-avatar" aria-hidden="true">{initials(fax.name)}</div>
                    <div>
                      <h6 className="mb-0 fw-bold text-dark">{fax.to_number}</h6>
                      <div className="text-muted small">{formatToEST(fax.date_time)}</div>
                    </div>
                  </div>
                  <div className="dropdown">
                    <button className="btn btn-sm btn-outline-primary fax-action-btn" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="window" aria-label="Fax actions">
                      <FaEllipsisV aria-hidden="true" />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                      {fax.document_link && (
                        <li>
                          <a href={fax.document_link} target="_blank" rel="noopener noreferrer" className="dropdown-item">
                            <FaFilePdf className="me-2 text-danger" aria-hidden="true" /> View PDF
                          </a>
                        </li>
                      )}
                      {fax.direction?.toLowerCase() !== 'inbound' && (
                        <li>
                          <button className="dropdown-item" onClick={() => handleOpenResend(fax)}>
                            <FaSync className="me-2" aria-hidden="true" /> Resend Fax
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                <div className="mb-2 small text-muted">
                  <span className="fw-medium">From:</span> {fax.from_number || 'N/A'}
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                  <span className={`badge dir-${fax.direction?.toLowerCase()}`}>
                    {fax.direction?.toUpperCase() || 'OUTBOUND'}
                  </span>
                  <span className={`badge status-${fax.status?.toLowerCase()}`}>
                    {fax.status?.toUpperCase() || 'UNKNOWN'}
                  </span>
                </div>
                <div className="text-muted small">
                  {fax.name} ({fax.email})
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="table-responsive d-none d-md-block">
        <table className="fax-table">
          <thead>
            <tr>
              <th>Date & Time</th>
              <th>Direction</th>
              <th>Sender</th>
              <th>From Number</th>
              <th>To Number</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {faxes.length === 0 ? (
              <tr>
                <td colSpan="7">
                  <div className="fax-empty-state">
                    <FaInbox className="fax-empty-icon" aria-hidden="true" />
                    <div className="fw-bold text-dark mt-2">No faxes found</div>
                    <div className="text-muted small">Sent and received faxes will appear here.</div>
                  </div>
                </td>
              </tr>
            ) : (
              faxes.map((fax) => (
                <tr key={fax.fax_id}>
                  <td>{formatToEST(fax.date_time)}</td>
                  <td>
                    <span className={`badge dir-${fax.direction?.toLowerCase()}`}>
                      {fax.direction?.toUpperCase() || 'OUTBOUND'}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <div className="fax-avatar fax-avatar-sm" aria-hidden="true">{initials(fax.name)}</div>
                      <div>
                        <div className="fw-medium text-dark">{fax.name}</div>
                        <div className="text-sm text-gray">{fax.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>{fax.from_number || 'N/A'}</td>
                  <td>{fax.to_number}</td>
                  <td>
                    <span className={`badge status-${fax.status?.toLowerCase()}`}>
                      {fax.status?.toUpperCase() || 'UNKNOWN'}
                    </span>
                  </td>
                  <td className="text-end">
                    <div className="dropdown">
                      <button className="btn btn-sm btn-outline-primary fax-action-btn" type="button" data-bs-toggle="dropdown" aria-expanded="false" data-bs-boundary="window" aria-label="Fax actions">
                        <FaEllipsisV aria-hidden="true" />
                      </button>
                      <ul className="dropdown-menu dropdown-menu-end shadow-sm">
                        {fax.document_link && (
                          <li>
                            <a href={fax.document_link} target="_blank" rel="noopener noreferrer" className="dropdown-item">
                              <FaFilePdf className="me-2 text-danger" aria-hidden="true" /> View PDF
                            </a>
                          </li>
                        )}
                        {fax.direction?.toLowerCase() !== 'inbound' && (
                          <li>
                            <button className="dropdown-item" onClick={() => handleOpenResend(fax)}>
                              <FaSync className="me-2" aria-hidden="true" /> Resend Fax
                            </button>
                          </li>
                        )}
                      </ul>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
    
    {resendModal.show && (
      <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content shadow-lg border-0 fax-modal">
            <div className="modal-header">
              <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                <FaSync aria-hidden="true" /> Resend Fax
              </h5>
              <button type="button" className="btn-close" onClick={handleCloseResend} disabled={isResending} aria-label="Close"></button>
            </div>
            <div className="modal-body p-4">
              <div className="alert alert-info small">
                You are about to resend the document originally sent on <strong>{formatToEST(resendModal.fax.date_time)}</strong>.
              </div>
              <div className="mb-1">
                <label className="form-label fw-bold">Destination Fax Number</label>
                <input type="text" className="form-control" value={resendModal.toNumber} onChange={(e) => setResendModal({ ...resendModal, toNumber: e.target.value })} placeholder="+1234567890" />
                <div className="form-text">You can retry the same number or provide a different one.</div>
              </div>
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-secondary fw-bold" onClick={handleCloseResend} disabled={isResending}>Cancel</button>
              <button type="button" className="btn btn-primary fw-bold" onClick={handleResendSubmit} disabled={isResending}>
                {isResending ? 'Sending…' : 'Confirm Resend'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </TaxPortalLayout>
  );
};

export default FaxDashboard;